import { ref, onMounted, watch, computed, reactive, nextTick } from 'vue'
import { nanoid } from 'nanoid'
import { getDefaultItem } from './items'
import api from '@/utils/ky'
import { confirm } from '@/utils/events'

function getDefaultBlock ({ lang, country }) {
  return {
    id: 'new_' + nanoid(),
    title: '',
    kind: 'big-carousel',
    lang,
    country,
    items: [getDefaultItem()]
  }
}

const blockKindMap = {
  'big-carousel': 'Большой слайдер',
  'medium-carousel': 'Средний слайдер',
  'small-carousel': 'Маленький слайдер',
  'single-card': 'Одна карточка',
  list: 'Список'
}

const fetchedBlocks = ref([])
const blocks = ref([])

export function useBlocks ({ router, route } = {}) {
  function prepareFetchedBlocks (blocks) {
    blocks.forEach(block => {
      block.items.forEach(item => {
        item.imageFile = null
      })
    })

    return blocks
  }

  function fetchBlocks () {
    const isFetchedBlocks = ref(false)

    onMounted(async () => {
      const { data } = await api.get('admin/v2/articles/blocks')

      fetchedBlocks.value = prepareFetchedBlocks(data)
      isFetchedBlocks.value = true
    })

    return { isFetchedBlocks }
  }

  function getBlocks (blockList = fetchedBlocks.value) {
    return structuredClone(
      blockList.filter(
        block =>
          block.lang === route?.params?.lang &&
          block.country === route?.params?.country
      )
    )
  }

  function getBlockById (id) {
    return blocks.value.find(block => block.id === id)
  }

  function addBlock () {
    const block = getDefaultBlock({
      lang: route.params.lang,
      country: route.params.country
    })
    blocks.value.push(block)

    router.push({
      name: 'articles-block-item',
      params: {
        id: block.id
      }
    })
  }

  async function createBlock (rawBlock) {
    const block = structuredClone(rawBlock)

    delete block.id

    for (const item of block.items) {
      delete item.id
      if (item.page === '') item.page = null
      if (item.kind === 'page') item.link = null
      if (item.kind === 'link') item.page = null

      if (!item.imageFile) continue

      const file = new FormData()
      file.append('file', item.imageFile)
      file.append('path', 'articles-items/')

      const { data } = await api.upload('admin/spaces/upload', file)
      if (!data.url) {
        return new Error('file upload error')
      }

      item.image = data.url
    }

    const { data } = await api.post('admin/v2/articles/blocks', block)
    fetchedBlocks.value = prepareFetchedBlocks(data)
    blocks.value = []
    await nextTick()
    blocks.value = getBlocks(data)

    if (route.name !== 'articles-blocks') {
      router.push({ name: 'articles-blocks' })
    }
  }

  async function updateBlock (rawBlock) {
    const block = structuredClone(rawBlock)

    for (const item of block.items) {
      if (item.id.startsWith('new_')) delete item.id
      if (item.page === '') item.page = null
      if (item.kind === 'page') item.link = null
      if (item.kind === 'link') item.page = null

      if (!item.imageFile) continue

      const file = new FormData()
      file.append('file', item.imageFile)
      file.append('path', 'articles-items/')

      const { data } = await api.upload('admin/spaces/upload', file)
      if (!data.url) {
        return new Error('file upload error')
      }

      item.image = data.url
    }

    const { data } = await api.patch(
      `admin/v2/articles/blocks/${block.id}`,
      block
    )
    fetchedBlocks.value = prepareFetchedBlocks(data)
    blocks.value = []
    await nextTick()
    blocks.value = getBlocks(data)

    if (route.name !== 'articles-blocks') {
      router.push({ name: 'articles-blocks' })
    }
  }

  async function deleteBlock (block) {
    const confirmed = await confirm('Точно удаляем?')
    if (!confirmed) return

    await api.delete(`admin/v2/articles/blocks/${block.id}`)

    const fetchedBlockIndex = fetchedBlocks.value.findIndex(
      b => b.id === block.id
    )
    if (fetchedBlockIndex > -1) {
      fetchedBlocks.value.splice(fetchedBlockIndex, 1)
    }

    const blocksIndex = blocks.value.findIndex(b => Object.is(b, block))
    blocks.value.splice(blocksIndex, 1)

    if (route.name !== 'articles-blocks') {
      router.push({ name: 'articles-blocks' })
    }
  }

  function addItem (block) {
    const item = getDefaultItem()
    block.items.push(item)

    router.push({
      name: 'articles-block-subitem',
      params: {
        itemId: item.id
      }
    })
  }

  async function deleteItem (block, item) {
    const confirmed = await confirm('Точно удаляем?')
    if (!confirmed) return

    if (!item.id.startsWith('new_')) {
      await api.delete(`admin/v2/articles/blocks/${block.id}/items/${item.id}`)
    }

    const fetchedBlock = fetchedBlocks.value.find(b => b.id === block.id)
    if (fetchedBlock != null) {
      const fetchedItemIndex = fetchedBlock.items.findIndex(
        i => i.id === item.id
      )
      if (fetchedItemIndex > -1) {
        fetchedBlock.items.splice(fetchedItemIndex, 1)
      }
    }

    const itemIndex = block.items.findIndex(blockItem =>
      Object.is(blockItem, item)
    )
    block.items.splice(itemIndex, 1)

    if (
      !(route.name === 'articles-block-item' && route.params.id === block.id)
    ) {
      router.push({
        name: 'articles-block-item',
        params: {
          id: block.id
        }
      })
    }
  }

  return {
    blockKindMap,
    blocks,
    fetchBlocks,
    getBlocks,
    getBlockById,

    addBlock,
    createBlock,
    updateBlock,
    deleteBlock,
    addItem,
    deleteItem
  }
}
